import { ValidationError } from '@shared/errors';
import { FileDownload, getDownloadFilename } from '@shared/http';
import Axios, { isAxiosError } from 'axios';

import { artiaProtectedApi, authRequestInterceptor } from '@/lib/api-client';
import { env } from '@/config/env';

/**
 * Request options for the write/download helpers. Mirrors the ergonomics the
 * retired HttpClient wrapper exposed so call sites read the same.
 */
export type RequestOptions<T = unknown> = {
  data?: T;
  headers?: Record<string, string>;
  params?: Record<string, string>;
};

const VALIDATION_ERROR_TITLE = 'Validation Error';
const EMPTY_VALIDATION_MESSAGE =
  'One or more validation errors occurred but the server did not provide any details.';

/**
 * Flattens a 400 response body into validation messages. Accepts either a bare
 * string[] or an ASP.NET `{ errors: { field: string | string[] } }` ProblemDetails.
 */
const parseValidationMessages = (body: unknown): string[] => {
  const messages: string[] = [];

  if (Array.isArray(body)) {
    body.forEach((message: string) => messages.push(message));
  } else if (body && typeof body === 'object' && 'errors' in body) {
    const errors = (body as { errors: Record<string, string | string[]> }).errors;
    Object.keys(errors).forEach((key) => {
      const value = errors[key];
      if (Array.isArray(value)) {
        value.forEach((message) => messages.push(message));
      } else {
        messages.push(value);
      }
    });
  }

  if (messages.length === 0) {
    messages.push(EMPTY_VALIDATION_MESSAGE);
  }

  return messages;
};

/**
 * Re-throws an axios failure as a ValidationError when the server returned a
 * 400 (so consumers render the field messages), otherwise re-throws as-is.
 */
const throwAsValidationError = (error: unknown): never => {
  if (isAxiosError(error) && error.response?.status === 400) {
    throw new ValidationError(parseValidationMessages(error.response.data), VALIDATION_ERROR_TITLE);
  }

  throw error;
};

const axiosConfig = (options?: RequestOptions) => ({
  headers: options?.headers,
  params: options?.params,
});

export const postWithValidation = async <T = void>(url: string, options?: RequestOptions): Promise<T> => {
  try {
    return await artiaProtectedApi.post(url, options?.data, axiosConfig(options));
  } catch (error) {
    return throwAsValidationError(error);
  }
};

export const putWithValidation = async <T = void>(url: string, options?: RequestOptions): Promise<T> => {
  try {
    return await artiaProtectedApi.put(url, options?.data, axiosConfig(options));
  } catch (error) {
    return throwAsValidationError(error);
  }
};

export const patchWithValidation = async <T = void>(url: string, options?: RequestOptions): Promise<T> => {
  try {
    return await artiaProtectedApi.patch(url, options?.data, axiosConfig(options));
  } catch (error) {
    return throwAsValidationError(error);
  }
};

export const deleteWithValidation = async <T = void>(url: string, options?: RequestOptions): Promise<T> => {
  try {
    return await artiaProtectedApi.delete(url, { ...axiosConfig(options), data: options?.data });
  } catch (error) {
    return throwAsValidationError(error);
  }
};

// Downloads need the raw response (blob body + Content-Disposition/Content-Type
// headers), so they use a dedicated instance without the data-unwrapping
// response interceptor that artiaProtectedApi applies.
const fileApi = Axios.create({ baseURL: env.BASE_API_URL });
fileApi.interceptors.request.use(authRequestInterceptor);

const isAbsoluteUrl = (url: string): boolean =>
  url.startsWith('https://') || url.startsWith('http://') || url.startsWith('//');

export const downloadFile = async (
  httpMethod: string,
  url: string,
  options?: RequestOptions,
): Promise<FileDownload> => {
  const response = await fileApi.request<Blob>({
    method: httpMethod,
    url,
    data: options?.data,
    ...axiosConfig(options),
    responseType: 'blob',
    // sb: we need to support absolute URLs for download links
    ...(isAbsoluteUrl(url) ? { baseURL: '' } : {}),
  });

  return {
    contents: response.data,
    contentType: response.headers['content-type'] as string,
    filename: getDownloadFilename(response.headers['content-disposition'] as string | undefined) as string,
  };
};
